import { parse } from "acorn";
import { full } from "acorn-walk";

// The code view (PRD ladder target 5, decision 26): student JavaScript as event cards, like
// Scratch hat blocks. Top-level code is "When the game starts"; addEventListener / onclick are
// "When X is clicked / a key is pressed"; setInterval / setTimeout are "Every / After N seconds";
// a requestAnimationFrame loop is "Every frame"; other functions are recipes the cards use.
// Labels here are deterministic; a cached Haiku call can replace them with friendlier wording.

// acorn's ESTree nodes, read loosely.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;

export type CardKind = "start" | "click" | "key" | "event" | "timer" | "delay" | "frame" | "recipe";

export interface CodeLine {
  /** Line number in the file (1-based). */
  n: number;
  text: string;
}

export interface Note {
  /** Index into the card's lines (1-based), the same numbering the label model sees. */
  line: number;
  text: string;
}

export interface EventCard {
  id: string;
  kind: CardKind;
  title: string;
  file: string;
  startLine: number;
  endLine: number;
  /** The card's code, in segments separated by a "…" line when they aren't next to each other. */
  lines: CodeLine[];
  steps: string[];
  notes: Note[];
  /** Key the preview's run counter reports for the card's function ("file:offset"), if it has one. */
  traceKey: string | null;
  /** Things (top-level variables) this card changes, including through the recipes it uses. */
  changes: string[];
  /** Things this card reads. */
  uses: string[];
  /** Recipes this card uses. */
  calls: string[];
}

export type ThingKind = "number" | "string" | "boolean" | "element" | "elements" | "list" | "object" | "drawing" | "function" | "other" | "none";

export interface Thing {
  name: string;
  file: string;
  line: number;
  declared: "const" | "let" | "var";
  kind: ThingKind;
  /** The starting value as written, shortened. */
  init: string;
}

export interface FileView {
  file: string;
  cards: EventCard[];
  things: Thing[];
  /** Keys passed to saveData(...) with a literal string. */
  savedKeys: string[];
  /** Set when the file doesn't parse; the cards are empty until it does. */
  error?: { message: string; line: number };
}

const PARSE_OPTIONS = { ecmaVersion: "latest" as const, locations: true, allowHashBang: true };

/** Same parse the preview's run counters use, so node offsets match. */
export function parseScript(code: string): { ast: N | null; error?: { message: string; line: number } } {
  try {
    return { ast: parse(code, { ...PARSE_OPTIONS, sourceType: "script" }) };
  } catch (e) {
    try {
      return { ast: parse(code, { ...PARSE_OPTIONS, sourceType: "module" }) };
    } catch {
      const err = e as { message?: string; loc?: { line: number } };
      return { ast: null, error: { message: String(err.message ?? "Syntax error").replace(/\s*\(\d+:\d+\)$/, ""), line: err.loc?.line ?? 1 } };
    }
  }
}

const isFn = (n: N) => n && (n.type === "FunctionExpression" || n.type === "ArrowFunctionExpression" || n.type === "FunctionDeclaration");
const memberName = (n: N): string | null => (n?.type === "MemberExpression" && !n.computed && n.property.type === "Identifier" ? n.property.name : null);
const calleeName = (call: N): string | null => (call.callee.type === "Identifier" ? call.callee.name : memberName(call.callee));
const strLit = (n: N): string | null => (n?.type === "Literal" && typeof n.value === "string" ? n.value : n?.type === "TemplateLiteral" && n.expressions.length === 0 ? n.quasis[0].value.cooked : null);
const numLit = (n: N): number | null => (n?.type === "Literal" && typeof n.value === "number" ? n.value : null);

/** The root identifier of `a.b.c` / `a[0]`, if any. */
function rootName(n: N): string | null {
  while (n?.type === "MemberExpression") n = n.object;
  return n?.type === "Identifier" ? n.name : null;
}

export function singular(word: string): string {
  if (/ies$/i.test(word)) return word.slice(0, -3) + "y";
  if (/(ss|sh|ch|x|z)es$/i.test(word)) return word.slice(0, -2);
  if (/s$/i.test(word) && !/ss$/i.test(word)) return word.slice(0, -1);
  return word;
}

const TAG_WORDS: Record<string, string> = { div: "box", span: "bit of text", p: "paragraph", button: "button", img: "picture", li: "list item", ul: "list", h1: "heading", h2: "heading", h3: "heading", input: "text box", canvas: "canvas", a: "link" };
/** A quoted text value, cut short so a card stays one glance (the closing quote is added by the caller). */
const quoteText = (t: string) => {
  const s = t.replace(/\s+/g, " ").trim();
  return s.length > 24 ? `${s.slice(0, 23)}…` : s;
};
const article = (w: string) => (/^[aeiou]/i.test(w) ? "an" : "a");

export function formatSeconds(ms: number): string {
  const s = Math.round((ms / 1000) * 100) / 100;
  return s === 1 ? "second" : `${s} seconds`;
}

const KEY_NAMES: Record<string, string> = {
  ArrowLeft: "left arrow",
  ArrowRight: "right arrow",
  ArrowUp: "up arrow",
  ArrowDown: "down arrow",
  " ": "space",
  Space: "space",
  Enter: "Enter",
  Escape: "Escape",
};
const keyName = (k: string) => KEY_NAMES[k] ?? (/^Key[A-Z]$/.test(k) ? k.slice(3) : /^Digit\d$/.test(k) ? k.slice(5) : k.length === 1 ? k.toUpperCase() : k);

export function analyzeFile(file: string, code: string): FileView {
  const { ast, error } = parseScript(code);
  if (!ast) return { file, cards: [], things: [], savedKeys: [], error };
  const fileLines = code.split("\n");
  const src = (n: N) => code.slice(n.start, n.end);
  const short = (n: N, max = 28) => {
    const s = src(n).replace(/\s+/g, " ").trim();
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  };
  // Plain words for a value, so cards read as what the program does rather than code
  // (Math.floor(Math.random() * 6) → "a random number", text.split("\n") → "the lines of text").
  const phrase = (n: N, max = 36): string => {
    const s = words(n);
    return s === null ? short(n, max) : s.length > max ? `${s.slice(0, max - 1)}…` : s;
  };
  const words = (n: N): string | null => {
    if (!n) return null;
    switch (n.type) {
      case "Identifier":
        return n.name;
      case "Literal":
        if (typeof n.value === "string") return n.value === "" ? "nothing" : n.value === "\n" ? "a new line" : /^\s+$/.test(n.value) ? "a space" : `“${quoteText(n.value)}”`;
        return n.value === null ? "nothing" : String(n.value);
      case "TemplateLiteral":
        return n.expressions.length === 0 ? words({ type: "Literal", value: n.quasis[0].value.cooked }) : "a message";
      case "AwaitExpression":
        return words(n.argument);
      case "ArrayExpression":
        return n.elements.length ? `a list of ${n.elements.length}` : "an empty list";
      case "ObjectExpression":
        return "a group of values";
      case "ArrowFunctionExpression":
      case "FunctionExpression":
        return "a recipe";
      case "ConditionalExpression":
        return `${phrase(n.consequent, 20)} or ${phrase(n.alternate, 20)}`;
      case "UnaryExpression":
        return n.operator === "!" ? `not ${phrase(n.argument, 24)}` : n.operator === "-" ? `minus ${phrase(n.argument, 24)}` : null;
      case "BinaryExpression": {
        if (n.operator === "+" && (strLit(n.left) !== null || strLit(n.right) !== null || n.left.type === "TemplateLiteral")) return "a message";
        if (n.operator === "*" && [n.left, n.right].some((x: N) => src(x) === "Math.random()")) return "a random number";
        const ops: Record<string, string> = { "+": "plus", "-": "minus", "*": "times", "/": "divided by" };
        if (n.operator === "%") return `the leftover from ${phrase(n.left, 16)} ÷ ${phrase(n.right, 16)}`;
        return ops[n.operator] ? `${phrase(n.left, 18)} ${ops[n.operator]} ${phrase(n.right, 18)}` : null;
      }
      case "MemberExpression": {
        if (n.computed) {
          const list = rootName(n.object) ?? "the list";
          return /^\d+$/.test(String(n.property.value ?? "")) ? `item ${Number(n.property.value) + 1} of ${list}` : `one of the ${list}`;
        }
        const prop = n.property.name as string;
        const obj = phrase(n.object, 20);
        if (prop === "value") return `what's in ${obj}`;
        if (prop === "length") return `how many ${obj}`;
        if (/^(textContent|innerText|innerHTML)$/.test(prop)) return `the words in ${obj}`;
        if (prop === "checked") return `whether ${obj} is ticked`;
        return `${obj}'s ${prop}`;
      }
      case "NewExpression":
        return n.callee.type === "Identifier" && n.callee.name === "Date" ? "the time right now" : null;
      case "CallExpression": {
        const name = calleeName(n);
        const obj = n.callee.type === "MemberExpression" ? n.callee.object : null;
        const arg = n.arguments[0];
        if (obj?.type === "Identifier" && obj.name === "Math") {
          if (name === "random") return "a random number";
          if (name === "abs") return `the size of ${phrase(arg, 24)}`;
          if (/^(floor|round|ceil|trunc)$/.test(name ?? "")) return src(arg).includes("Math.random") ? "a random number" : `${phrase(arg, 24)}, rounded`;
          if (name === "max") return "the biggest of some numbers";
          if (name === "min") return "the smallest of some numbers";
          return null;
        }
        if (obj?.type === "Identifier" && obj.name === "Date" && name === "now") return "the time right now";
        if (name === "setTimeout" || name === "setInterval") return "a new timer";
        if (name === "Number" || name === "parseInt" || name === "parseFloat") return `the number in ${phrase(arg, 24)}`;
        if (name === "String") return phrase(arg);
        if (name === "getElementById" || name === "querySelector") return `the ${(strLit(arg) ?? "thing").replace(/^[#.]/, "")} on the page`;
        if (name === "querySelectorAll" || (name && /^getElementsBy/.test(name))) return `all the ${(strLit(arg) ?? "things").replace(/^[#.]/, "")} on the page`;
        if (name === "createElement") return `a new ${TAG_WORDS[strLit(arg) ?? ""] ?? "piece of the page"}`;
        if (name === "getData") return `live ${strLit(arg) ?? ""} data`.replace("  ", " ");
        if (name === "loadData") return `${strLit(arg) ?? "something"} saved from last time`;
        if (obj && /^(trim|toLowerCase|toUpperCase|toString|toFixed)$/.test(name ?? "")) return phrase(obj);
        if (obj && name === "split") return strLit(arg) === "\n" ? `the lines of ${phrase(obj, 20)}` : `the pieces of ${phrase(obj, 20)}`;
        if (obj && name === "slice") return n.arguments.length ? `part of ${phrase(obj, 24)}` : `a copy of ${phrase(obj, 24)}`;
        if (obj && name === "join") return `${phrase(obj, 24)}, joined together`;
        if (obj && name === "includes") return `whether ${phrase(obj, 16)} has ${phrase(arg, 16)}`;
        if (obj && (name === "filter" || name === "map")) return `${phrase(obj, 24)}, changed`;
        if (obj && name === "find") return `one of the ${phrase(obj, 24)}`;
        if (obj && name === "indexOf") return `where ${phrase(arg, 16)} is in ${phrase(obj, 16)}`;
        if (n.callee.type === "Identifier") return `what ${n.callee.name} gives back`;
        return null;
      }
      default:
        return null;
    }
  };

  // ---- Things and named functions at the top level ----
  const things: Thing[] = [];
  const functions = new Map<string, { fn: N; stmt: N }>();
  for (const stmt of ast.body) {
    if (stmt.type === "FunctionDeclaration" && stmt.id) functions.set(stmt.id.name, { fn: stmt, stmt });
    if (stmt.type === "VariableDeclaration") {
      for (const d of stmt.declarations) {
        if (d.id.type !== "Identifier") continue;
        if (isFn(d.init)) {
          functions.set(d.id.name, { fn: d.init, stmt });
          continue;
        }
        things.push({ name: d.id.name, file, line: d.loc.start.line, declared: stmt.kind, kind: thingKind(d.init), init: d.init ? short(d.init, 40) : "" });
      }
    }
  }
  const thingNames = new Set(things.map((t) => t.name));
  const startValue = (arg: N): number | null => {
    if (arg?.type !== "Identifier") return null;
    for (const stmt of ast.body) {
      if (stmt.type !== "VariableDeclaration") continue;
      for (const d of stmt.declarations) if (d.id.type === "Identifier" && d.id.name === arg.name) return numLit(d.init);
    }
    return null;
  };

  // ---- Registrations: event listeners, timers, animation frames ----
  interface Reg {
    kind: CardKind;
    title: string;
    handler: N | null; // the function that runs
    handlerName: string | null; // when passed by name
    stmt: N; // the top-level statement holding the registration
    at: N; // the registering call / assignment
  }
  const regs: Reg[] = [];
  const topOf = (n: N): N => ast.body.find((s: N) => s.start <= n.start && n.end <= s.end) ?? n;
  const resolve = (h: N): { fn: N | null; name: string | null } => {
    if (isFn(h)) return { fn: h, name: null };
    if (h?.type === "Identifier") return { fn: functions.get(h.name)?.fn ?? null, name: h.name };
    return { fn: null, name: null };
  };

  // Loop variables: forEach callbacks and for…of loops, so `hole` reads as "a hole" from `holes`.
  const loopVars = new Map<N, string>(); // identifier-binding node -> list name
  full(ast, (n: N) => {
    if (n.type === "CallExpression" && memberName(n.callee) === "forEach" && isFn(n.arguments[0])) {
      const list = rootName(n.callee.object);
      const p = n.arguments[0].params[0];
      if (list && p?.type === "Identifier") loopVars.set(n.arguments[0], `${p.name}\u0000${list}`);
    }
    if (n.type === "ForOfStatement" && n.left.type === "VariableDeclaration") {
      const id = n.left.declarations[0]?.id;
      const list = rootName(n.right);
      if (id?.type === "Identifier" && list) loopVars.set(n, `${id.name}\u0000${list}`);
    }
  });
  const targetPhrase = (target: N, at: N): string => {
    const name = target.type === "Identifier" ? target.name : null;
    if (name === "document" || name === "window" || name === "document.body") return "the page";
    if (name) {
      for (const [scope, v] of loopVars) {
        const [variable, list] = v.split("\u0000") as [string, string];
        if (variable === name && scope.start <= at.start && at.end <= scope.end) return `${article(singular(list))} ${singular(list)}`;
      }
      return name;
    }
    if (target.type === "CallExpression") {
      const sel = strLit(target.arguments[0]);
      if (sel) return `the ${sel.replace(/^[#.]/, "")} element`;
    }
    if (memberName(target) === "body") return "the page";
    return short(target, 24);
  };

  const eventTitle = (type: string, target: N, at: N, fn: N | null): { kind: CardKind; title: string } => {
    const what = targetPhrase(target, at);
    if (/^key(down|up|press)$/.test(type)) {
      const keys = fn ? keysChecked(fn) : [];
      const verb = type === "keyup" ? "released" : "pressed";
      if (keys.length === 1) return { kind: "key", title: `When the ${keyName(keys[0]!)} key is ${verb}` };
      if (keys.length > 1 && keys.every((k) => k.startsWith("Arrow"))) return { kind: "key", title: `When an arrow key is ${verb}` };
      return { kind: "key", title: `When a key is ${verb}` };
    }
    const phrases: Record<string, string> = {
      click: `When ${what} is clicked`,
      dblclick: `When ${what} is double-clicked`,
      mousedown: `When ${what} is pressed`,
      mouseup: `When ${what} is let go`,
      mousemove: `When the mouse moves over ${what}`,
      mouseover: `When the mouse goes over ${what}`,
      mouseenter: `When the mouse goes over ${what}`,
      mouseleave: `When the mouse leaves ${what}`,
      input: `When ${what} is typed in`,
      change: `When ${what} changes`,
      submit: `When ${what} is sent`,
      load: "When the page loads",
      DOMContentLoaded: "When the page loads",
      touchstart: `When ${what} is touched`,
    };
    return { kind: type === "click" || type === "dblclick" ? "click" : "event", title: phrases[type] ?? `When ${what} gets a ${type}` };
  };

  full(ast, (n: N) => {
    if (n.type === "CallExpression") {
      const name = calleeName(n);
      if (memberName(n.callee) === "addEventListener" && n.arguments.length >= 2) {
        const type = strLit(n.arguments[0]);
        if (!type) return;
        const { fn, name: hn } = resolve(n.arguments[1]);
        regs.push({ ...eventTitle(type, n.callee.object, n, fn), handler: fn, handlerName: hn, stmt: topOf(n), at: n });
      } else if ((name === "setInterval" || name === "setTimeout") && n.arguments.length >= 1 && (n.callee.type === "Identifier" || rootName(n.callee) === "window")) {
        const { fn, name: hn } = resolve(n.arguments[0]);
        // A delay held in a top-level variable reads as its starting value: the timer keeps the
        // value it was started with even if the variable changes later.
        const ms = numLit(n.arguments[1]) ?? startValue(n.arguments[1]) ?? (n.arguments[1] ? null : 0);
        // A setTimeout that schedules its own function again works like setInterval.
        const repeats = name === "setInterval" || (fn && enclosingFn(n) === fn);
        const when = ms === null ? (repeats ? "Every so often" : "After a moment") : repeats ? `Every ${formatSeconds(ms)}` : ms === 0 ? "Right after the game starts" : `After ${formatSeconds(ms).replace(/^second$/, "1 second")}`;
        regs.push({ kind: repeats ? "timer" : "delay", title: when, handler: fn, handlerName: hn, stmt: topOf(n), at: n });
      } else if (name === "requestAnimationFrame" && n.arguments.length >= 1) {
        const { fn, name: hn } = resolve(n.arguments[0]);
        regs.push({ kind: "frame", title: "Every frame", handler: fn, handlerName: hn, stmt: topOf(n), at: n });
      }
    } else if (n.type === "AssignmentExpression" && n.operator === "=") {
      const prop = memberName(n.left);
      if (prop && /^on[a-z]+$/.test(prop) && (isFn(n.right) || n.right.type === "Identifier")) {
        const { fn, name: hn } = resolve(n.right);
        if (!fn && !hn) return;
        regs.push({ ...eventTitle(prop.slice(2), n.left.object, n, fn), handler: fn, handlerName: hn, stmt: topOf(n), at: n });
      }
    }
  });

  /** True when the call sits inside some function (so it runs again each time that function runs). */
  function nestedIn(n: N): boolean {
    return !!enclosingFn(n) && !loopVars.has(enclosingFn(n));
  }

  function enclosingFn(n: N): N | null {
    let best: N | null = null;
    for (const { fn } of functions.values()) if (fn.start <= n.start && n.end <= fn.end && (!best || fn.start > best.start)) best = fn;
    // Inline function expressions count too.
    full(ast, (m: N) => {
      if (isFn(m) && m.start <= n.start && n.end <= m.end && (!best || m.start > best.start)) best = m;
    });
    return best;
  }

  // ---- Cards ----
  const cards: EventCard[] = [];
  const handlerCards = new Map<N, EventCard>();
  const linesOf = (nodes: N[]): CodeLine[] => {
    const spans = nodes
      .map((n) => [n.loc.start.line, n.loc.end.line] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .reduce<[number, number][]>((acc, s) => {
        const last = acc[acc.length - 1];
        if (last && s[0] <= last[1] + 1) last[1] = Math.max(last[1], s[1]);
        else acc.push([...s]);
        return acc;
      }, []);
    const out: CodeLine[] = [];
    spans.forEach(([a, b], i) => {
      if (i > 0) out.push({ n: 0, text: "…" });
      for (let l = a; l <= b; l++) out.push({ n: l, text: fileLines[l - 1] ?? "" });
    });
    return out;
  };
  const fnStatements = (fn: N): N[] => (fn.body.type === "BlockStatement" ? fn.body.body : [{ type: "ExpressionStatement", expression: fn.body, start: fn.body.start, end: fn.body.end, loc: fn.body.loc }]);
  const registrationOnly = new Set<N>(); // top-level statements that only register cards

  // The statement directly holding a call, for registrations made inside another card.
  const stmtOf = (at: N): N => {
    let best: N = r0(at);
    full(ast, (m: N) => {
      if (/Statement$|Declaration$/.test(m.type) && m.type !== "BlockStatement" && m.start <= at.start && at.end <= m.end && m.end - m.start < best.end - best.start) best = m;
    });
    return best;
  };
  const r0 = (at: N) => topOf(at);
  // Registered from inside a function other than a forEach callback (e.g. a timer started by a click)?
  const nested = (at: N) => {
    const f = enclosingFn(at);
    return !!f && !loopVars.has(f);
  };

  for (const r of regs) {
    // A recursive requestAnimationFrame / setTimeout inside the handler is the loop itself, not a new card.
    if (r.handler && handlerCards.has(r.handler)) continue;
    const isNested = nested(r.at);
    if (isNested) r.stmt = stmtOf(r.at);
    const segments: N[] = [r.stmt];
    const traceFn: N | null = r.handler;
    if (r.handler && !(r.stmt.start <= r.handler.start && r.handler.end <= r.stmt.end)) {
      const decl = [...functions.values()].find((f) => f.fn === r.handler)?.stmt;
      if (decl) segments.push(decl);
    }
    // A frame loop started from the top level ("loop();" / "requestAnimationFrame(loop)") is shown in its function.
    if (r.kind === "frame" && r.handler && r.handler.start <= r.at.start && r.at.end <= r.handler.end) {
      segments.splice(0, segments.length, ...[[...functions.values()].find((f) => f.fn === r.handler)?.stmt ?? r.stmt]);
    }
    if (!isNested && r.stmt.type === "ExpressionStatement" && !(r.handler && r.stmt.start <= r.handler.start && r.handler.end <= r.stmt.end && r.kind === "frame")) registrationOnly.add(r.stmt);
    const body = r.handler ? fnStatements(r.handler).filter((s: N) => !(r.kind === "frame" && isRegistration(s, r.handler)) && !(r.kind === "timer" && isRegistration(s, r.handler))) : [];
    const span = linesOf(segments);
    // The card shows what runs (the handler's body), like the design; the span covers the registration too.
    const lines = r.handler && body.length ? linesOf(body) : span;
    const steps = r.handler ? stepsFor(body) : r.handlerName ? [`do ${r.handlerName}`] : [];
    if (r.kind === "frame") steps.push("then wait for the next frame and do it again");
    const card: EventCard = {
      id: `${file}:${r.kind}:${(r.handler ?? r.at).start}`,
      kind: r.kind,
      title: r.title,
      file,
      startLine: span.find((l) => l.n)!.n,
      endLine: span[span.length - 1]!.n,
      lines,
      steps: steps.slice(0, 5),
      notes: notesFor(r.handler ? body : [r.at], lines),
      traceKey: traceFn ? `${file}:${traceFn.start}` : null,
      changes: [],
      uses: [],
      calls: [],
    };
    if (r.handler) {
      collectEffects(r.handler, card);
      handlerCards.set(r.handler, card);
    } else if (r.handlerName) card.calls.push(r.handlerName);
    cards.push(card);
  }

  function isRegistration(s: N, fn: N): boolean {
    const e = s.type === "ExpressionStatement" ? s.expression : null;
    if (e?.type !== "CallExpression") return false;
    const name = calleeName(e);
    if (name !== "requestAnimationFrame" && name !== "setTimeout") return false;
    return resolve(e.arguments[0]).fn === fn;
  }

  // Recipes: named functions no registration runs directly.
  const recipeCards = new Map<string, EventCard>();
  for (const [name, { fn, stmt }] of functions) {
    if (handlerCards.has(fn)) continue;
    const span = linesOf([stmt]);
    const body = fnStatements(fn);
    const lines = body.length ? linesOf(body) : span;
    const card: EventCard = {
      id: `${file}:recipe:${fn.start}`,
      kind: "recipe",
      title: `Recipe: ${name}`,
      file,
      startLine: span[0]!.n,
      endLine: span[span.length - 1]!.n,
      lines,
      steps: stepsFor(body).slice(0, 5),
      notes: notesFor(body, lines),
      traceKey: `${file}:${fn.start}`,
      changes: [],
      uses: [],
      calls: [],
    };
    collectEffects(fn, card);
    recipeCards.set(name, card);
  }

  // "When the game starts": every top-level statement that isn't a function or only a registration.
  const startStmts = ast.body.filter((s: N) => {
    if (s.type === "FunctionDeclaration" || registrationOnly.has(s)) return false;
    if (s.type === "VariableDeclaration" && s.declarations.every((d: N) => isFn(d.init))) return false;
    return true;
  });
  if (startStmts.length) {
    const lines = linesOf(startStmts);
    const card: EventCard = {
      id: `${file}:start`,
      kind: "start",
      title: "When the game starts",
      file,
      startLine: lines[0]!.n,
      endLine: lines[lines.length - 1]!.n,
      lines,
      steps: stepsFor(startStmts).slice(0, 5),
      notes: notesFor(startStmts, lines),
      traceKey: null,
      changes: [],
      uses: [],
      calls: [],
    };
    for (const s of startStmts) collectEffects(s, card);
    cards.unshift(card);
  }

  // A card changes whatever the recipes it uses change.
  const all = [...cards, ...recipeCards.values()];
  const deep = (c: EventCard, seen = new Set<string>()): string[] => {
    const out = new Set(c.changes);
    for (const name of c.calls) {
      if (seen.has(name)) continue;
      seen.add(name);
      const r = recipeCards.get(name);
      if (r) for (const t of deep(r, seen)) out.add(t);
    }
    return [...out];
  };
  for (const c of all) c.changes = deep(c).filter((t) => thingNames.has(t));
  for (const c of all) c.uses = [...new Set(c.uses)].filter((t) => thingNames.has(t) && !c.changes.includes(t));

  const savedKeys = new Set<string>();
  full(ast, (n: N) => {
    if (n.type === "CallExpression" && calleeName(n) === "saveData") {
      const k = strLit(n.arguments[0]);
      if (k) savedKeys.add(k);
    }
  });

  return { file, cards: [...cards, ...recipeCards.values()], things, savedKeys: [...savedKeys] };

  // ---- helpers that need the file's source ----

  function collectEffects(root: N, card: EventCard) {
    // Code inside another card's handler runs when that card's event happens, not this one's.
    const nestedHandlers = regs.map((r) => r.handler).filter((h) => h && h !== root && root.start <= h.start && h.end <= root.end);
    const inNested = (n: N) => nestedHandlers.some((h) => h.start <= n.start && n.end <= h.end);
    full(root, (n: N) => {
      if (inNested(n)) return;
      if (n.type === "AssignmentExpression") {
        const r = rootName(n.left);
        if (r) card.changes.push(r);
      } else if (n.type === "UpdateExpression") {
        const r = rootName(n.argument);
        if (r) card.changes.push(r);
      } else if (n.type === "CallExpression") {
        const name = calleeName(n);
        if (n.callee.type === "Identifier" && name && functions.has(name)) card.calls.push(name);
        const m = memberName(n.callee);
        // Methods that change the thing they're called on.
        if (m && /^(push|pop|shift|unshift|splice|sort|reverse|fill|add|remove|toggle|replace|append|appendChild|prepend|removeChild|replaceChildren|remove|setAttribute|removeAttribute|set|delete|clear)$/.test(m)) {
          const r = rootName(n.callee.object);
          if (r) card.changes.push(r);
        }
      } else if (n.type === "Identifier") {
        card.uses.push(n.name);
      }
    });
    card.calls = [...new Set(card.calls)];
  }

  function keysChecked(fn: N): string[] {
    const keys = new Set<string>();
    full(fn, (n: N) => {
      if (n.type === "BinaryExpression" && /^[!=]==?$/.test(n.operator)) {
        for (const [a, b] of [
          [n.left, n.right],
          [n.right, n.left],
        ]) {
          const prop = memberName(a);
          const k = strLit(b);
          if ((prop === "key" || prop === "code") && k !== null) keys.add(k);
        }
      }
      if (n.type === "SwitchCase" && n.test) {
        const k = strLit(n.test);
        if (k !== null) keys.add(k);
      }
    });
    return [...keys];
  }

  function condPhrase(t: N): string {
    if (t.type === "BinaryExpression") {
      const ops: Record<string, string> = { "===": "is", "==": "is", "!==": "isn't", "!=": "isn't", ">": "is more than", "<": "is less than", ">=": "is at least", "<=": "is at most" };
      const keyProp = memberName(t.left);
      if ((keyProp === "key" || keyProp === "code") && strLit(t.right) !== null && /^(===|==|!==|!=)$/.test(t.operator))
        return `the ${keyName(strLit(t.right)!)} key is ${t.operator.startsWith("!") ? "not " : ""}pressed`;
      if (t.left.type === "BinaryExpression" && t.left.operator === "%" && numLit(t.right) === 0 && /^(===|==)$/.test(t.operator))
        return `${phrase(t.left.left, 24)} is a multiple of ${phrase(t.left.right, 12)}`;
      if (ops[t.operator] && strLit(t.right) === "") return `${phrase(t.left, 20)} ${/^(!==|!=)$/.test(t.operator) ? "isn't" : "is"} empty`;
      if (ops[t.operator]) return `${phrase(t.left, 30)} ${ops[t.operator]} ${phrase(t.right, 24)}`;
    }
    if (t.type === "LogicalExpression") return `${condPhrase(t.left)} ${t.operator === "&&" ? "and" : "or"} ${condPhrase(t.right)}`;
    if (t.type === "UnaryExpression" && t.operator === "!") return `there's no ${phrase(t.argument, 20)}`;
    if (t.type === "Identifier" || t.type === "MemberExpression") return `there's ${article(short(t, 20))} ${short(t, 20)}`;
    return phrase(t, 30);
  }

  function stepsFor(stmts: N[], depth = 0): string[] {
    const out: string[] = [];
    const push = (s: string | null) => {
      if (!s) return;
      if (out[out.length - 1] === s) return;
      // "find the holes" + "find the scoreDisplay" -> "find the holes and the scoreDisplay"
      const last = out[out.length - 1];
      if (last?.startsWith("find the ") && s.startsWith("find the ")) out[out.length - 1] = `${last} and ${s.slice(5)}`;
      else out.push(s);
    };
    for (const s of stmts) push(stepFor(s, depth));
    return out;
  }

  function inner(body: N, depth: number): string {
    const list = stepsFor(body.type === "BlockStatement" ? body.body : [body], depth + 1);
    if (list.length === 0) return "nothing yet";
    return list.slice(0, 3).join(", ") + (list.length > 3 ? ", …" : "");
  }

  function stepFor(s: N, depth: number): string | null {
    switch (s.type) {
      case "VariableDeclaration":
        return s.declarations.map((d: N) => declStep(d)).filter(Boolean).join(", ") || null;
      case "ExpressionStatement":
        return exprStep(s.expression, depth);
      case "IfStatement": {
        let text = `if ${condPhrase(s.test)}: ${inner(s.consequent, depth)}`;
        if (s.alternate) text += s.alternate.type === "IfStatement" ? `; otherwise ${stepFor(s.alternate, depth)}` : `; otherwise: ${inner(s.alternate, depth)}`;
        return text;
      }
      case "ForOfStatement":
        return `for each ${s.left.declarations?.[0]?.id?.name ?? "item"} in ${phrase(s.right, 24)}: ${inner(s.body, depth)}`;
      case "ForStatement":
        return `repeat: ${inner(s.body, depth)}`;
      case "WhileStatement":
      case "DoWhileStatement":
        return `repeat while ${condPhrase(s.test)}: ${inner(s.body, depth)}`;
      case "ReturnStatement":
        return depth === 0 ? (s.argument ? `give back ${phrase(s.argument)}` : "stop here") : s.argument ? `give back ${phrase(s.argument)}` : "stop";
      case "FunctionDeclaration":
        return null;
      case "BlockStatement":
        return inner(s, depth - 1);
      case "SwitchStatement":
        return `check ${phrase(s.discriminant, 20)} and do different things`;
      default:
        return null;
    }
  }

  function declStep(d: N): string | null {
    if (d.id.type !== "Identifier") return null;
    const name = d.id.name;
    const init = d.init;
    if (!init) return `make ${name}`;
    if (isFn(init)) return null;
    if (init.type === "CallExpression") {
      const m = calleeName(init);
      if (m === "querySelectorAll" || (m && /^getElementsBy/.test(m))) return `find the ${name}`;
      if (m === "getElementById" || m === "querySelector") return `find the ${name}`;
      if (m === "getContext") return `get ready to draw on ${rootName(init.callee) ?? "the canvas"}`;
      if (m === "loadData") return `load ${name} saved from last time`;
    }
    if (init.type === "ArrayExpression") return init.elements.length ? `make the list ${name}` : `start ${name} as an empty list`;
    if (init.type === "ObjectExpression") return `make ${name}`;
    if (init.type === "Literal" && init.value === null) return `${name} starts empty`;
    return `set ${name} to ${phrase(init)}`;
  }

  function exprStep(e: N, depth: number): string | null {
    if (e.type === "AssignmentExpression") {
      const target = phrase(e.left, 24);
      const prop = memberName(e.left);
      if (prop && /^(textContent|innerText|innerHTML)$/.test(prop)) {
        const obj = short(e.left.object, 20);
        const v = strLit(e.right);
        return v === "" ? `clear ${obj}` : `show ${phrase(e.right)} in ${obj}`;
      }
      if (prop === "fillStyle" || prop === "strokeStyle") return "pick a color to draw with";
      if (e.left.type === "MemberExpression" && memberName(e.left.object) === "style") return `change how ${short(e.left.object.object, 20)} looks`;
      if (prop && /^on[a-z]+$/.test(prop)) return null;
      if (e.operator === "+=") return `add ${phrase(e.right, 20)} to ${target}`;
      if (e.operator === "-=") return `take ${phrase(e.right, 20)} from ${target}`;
      if (e.operator === "=" && e.right.type === "BinaryExpression" && src(e.right.left) === src(e.left)) {
        if (e.right.operator === "+") return `add ${phrase(e.right.right, 20)} to ${target}`;
        if (e.right.operator === "-") return `take ${phrase(e.right.right, 20)} from ${target}`;
      }
      if (e.right.type === "Literal" && e.right.value === null) return `empty ${target}`;
      return `set ${target} to ${phrase(e.right)}`;
    }
    if (e.type === "UpdateExpression") return `${e.operator === "++" ? "add 1 to" : "take 1 from"} ${short(e.argument, 24)}`;
    if (e.type === "CallExpression") {
      const name = calleeName(e);
      const obj = e.callee.type === "MemberExpression" ? e.callee.object : null;
      if (obj && rootName(obj) === "console") return "write a message in the console";
      if (name === "preventDefault" || name === "stopPropagation") return null;
      if (name === "alert") return "pop up a message";
      if (name === "clearTimeout" || name === "clearInterval") return "stop the timer";
      if (obj && (name === "appendChild" || name === "append" || name === "prepend")) return `put ${phrase(e.arguments[0], 20)} on the page`;
      if ((name === "remove" && obj) || name === "removeChild") return `take ${phrase(name === "remove" ? obj : e.arguments[0], 20)} off the page`;
      if (name === "saveData") return `remember ${strLit(e.arguments[0]) ?? "something"} for next time`;
      if (name === "addEventListener" || name === "setInterval" || name === "requestAnimationFrame" || /^on[a-z]+$/.test(name ?? "")) {
        // Inside a card, registering another card is a step worth seeing (it can happen again and again).
        const reg = regs.find((r) => r.at === e);
        return reg && nestedIn(e) ? `set up the card “${reg.title}”` : null;
      }
      if (name === "setTimeout") return `wait ${numLit(e.arguments[1]) !== null ? formatSeconds(numLit(e.arguments[1])!).replace(/^second$/, "1 second") : "a moment"}, then do more`;
      if (name === "forEach" && obj && isFn(e.arguments[0])) {
        const list = short(obj, 20);
        return `for each ${singular(list)} in ${list}: ${inner(e.arguments[0].body, depth)}`;
      }
      if (obj && memberName(e.callee.object) === "classList") return `change how ${short(e.callee.object.object, 20)} looks`;
      if (obj && rootName(obj) === "ctx") return "draw on the canvas";
      if (name && /^(fillRect|strokeRect|clearRect|fillText|arc|fill|stroke|drawImage|beginPath)$/.test(name)) return "draw on the canvas";
      if (name === "push" || name === "unshift") return `add to ${phrase(obj, 24)}`;
      if (name === "splice" || name === "pop" || name === "shift") return `remove from ${phrase(obj, 24)}`;
      if (e.callee.type === "Identifier" && name && functions.has(name)) return handlerCards.has(functions.get(name)!.fn) ? `start ${name}` : `do ${name}`;
      if (e.callee.type === "Identifier" && name) return `do ${name}`;
      return phrase(e, 30);
    }
    return null;
  }

  function notesFor(stmts: N[], lines: CodeLine[]): Note[] {
    const index = (fileLine: number) => lines.findIndex((l) => l.n === fileLine) + 1;
    const notes: Note[] = [];
    const flat: N[] = [];
    for (const s of stmts) {
      flat.push(s);
      const inner = s.type === "IfStatement" ? s.consequent : /^(For|ForOf|ForIn|While|DoWhile)Statement$/.test(s.type) ? s.body : null;
      if (inner) flat.push(...(inner.type === "BlockStatement" ? inner.body : [inner]));
    }
    for (const s of flat) {
      let text: string | null = null;
      if (s.type === "IfStatement") text = `if checks: ${condPhrase(s.test)}?`;
      else if (s.type === "CallExpression") text = exprStep(s, 1);
      else if (/^(For|ForOf|While|DoWhile)Statement$/.test(s.type)) text = s.type === "ForOfStatement" ? `goes through each one in ${phrase(s.right, 24)}` : "repeats the lines inside";
      else text = stepFor(s, 1);
      if (!text) continue;
      const at = index(s.loc.start.line);
      if (at > 0 && !notes.some((n) => n.line === at)) notes.push({ line: at, text: text.length > 60 ? `${text.slice(0, 59)}…` : text });
      if (notes.length >= 4) break;
    }
    return notes;
  }
}

function thingKind(init: N): ThingKind {
  if (!init) return "none";
  if (init.type === "Literal") return init.value === null ? "none" : (typeof init.value as ThingKind);
  if (init.type === "UnaryExpression" && init.argument.type === "Literal" && typeof init.argument.value === "number") return "number";
  if (init.type === "TemplateLiteral") return "string";
  if (init.type === "ArrayExpression") return "list";
  if (init.type === "ObjectExpression") return "object";
  if (init.type === "CallExpression") {
    const m = calleeName(init);
    if (m === "querySelectorAll" || (m && /^getElementsBy/.test(m))) return "elements";
    if (m === "getElementById" || m === "querySelector" || m === "createElement") return "element";
    if (m === "getContext") return "drawing";
  }
  return "other";
}
